/**
 * Om — a 4H swing strategy built on the three indicators already in the owner's
 * chart template (CLN candles, MM with a CLH source, AV_Band slow HMA).
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 *   REGIME  the AV_Band SLOW-HMA colour rule, verbatim from TradingChart's
 *           `slowHueAt`:  UP when linreg(close, 50) > slowHMA AND slowHMA rising,
 *           DOWN when both are below/falling, NEUTRAL otherwise. This is the
 *           state the template already PAINTS on the slow line — Om did not
 *           invent it, it reads it.
 *   BUY     the bar the regime FLIPS to UP, and the Daily CLN regression agrees.
 *   SHORT   the mirror.
 *   SELL /  the MM (CLH) stair on the trade's side is broken, OR 30 bars (5 days)
 *   COVER   elapse, OR the regime turns to the OPPOSITE direction. NEUTRAL does
 *           NOT close a position.
 *
 * ── WHY THESE PIECES, AND NOT THE ONES ORIGINALLY PLANNED ───────────────────
 * The design started as "regime as a filter, MM break as the trigger". A
 * pre-rule ablation over 480,000 4H bars on 48 symbols (2019-10 → 2026-09) said
 * that was wrong, so it was changed before anything was built:
 *
 *   • the regime as a standing FILTER is worthless — +0.08%/5d on its own, and
 *     −0.06% when added to the daily gate. As a TIMING event it is the best
 *     thing here: flip + gate returned +1.58%/5d on 79% of symbols.
 *   • the MM break as an ENTRY was worse than the flip (+1.02% vs +1.58%), so
 *     MM was demoted to the trailing stop, where the backtest ablation then
 *     showed it DOES earn its place (removing it costs 0.27% of TEST expectancy).
 *   • the Daily CLN gate is the workhorse: removing it costs 0.20% of TEST
 *     expectancy and takes max drawdown from 7.4% to 59.9%.
 *
 * Backtest (backend/src/scripts/om/backtestOm.ts, re-runnable, 0.20% round-trip
 * costs, time-split at 0.65): all 48 symbols expectancy +0.92%/trade, PF 1.38,
 * breadth 35/48; on the liquid majors +1.08%, PF 1.49, 21/25, TEST +0.42%.
 * Bhairav 4H on the same bars: +0.53% (majors +0.79%), TEST −0.12%.
 * Honest caveat carried in the report, not hidden: 76% of exits are stop fills
 * and the sim fills them AT the level — at +100bps of stop slippage expectancy
 * falls to +0.16% (majors +0.33%).
 *
 * ── WHY THE LENGTHS ARE PINNED ──────────────────────────────────────────────
 * clnLength 50 / slowPeriod 100 / MM source CLH are the OWNER'S template values,
 * chosen before any of this was measured, and Om pins them rather than following
 * Chart Settings. That way the strategy drawn on the chart is always the one that
 * was tested, and changing a display setting can never silently move the signals.
 * They are not a fitted optimum and should not be read as one — the backtest's
 * own sensitivity rows show clnLength 30 and 80 both turn TEST negative, so this
 * rule is NOT flat in its parameters. See the report.
 *
 * ── CAUSALITY ───────────────────────────────────────────────────────────────
 * Every series is computed from data up to bar i only. The chart passes CLOSED
 * bars, the daily gate reads the last daily bar whose close is at or before the
 * 4H bar's close, and the forming daily bar is dropped. Nothing repaints.
 *
 * Mirror of backend/src/services/strategies/omRules.ts — keep the two in sync.
 */
import { CandleRow } from '../../components/charts/TradingChart';
import {
  ChartStrategy, ChartStrategyOptions, StrategyOverlayResult, CandleColor, ChartMarker, TrendSeriesPoint,
} from './types';
import { transformCandles, linreg, dropFormingHtfBars } from '../candleTransforms';
import { hmaSeries } from '../hma';
import { computeWVol } from '../wvol';
import { computeBbrReversals, computeVolumeSpikes } from '../dailyBlastIndicators';
import { computeClnBars } from '../clnCandles';

const GREEN = '#26a69a';
const RED = '#ef5350';
const NEUTRAL = '#787b86';
const MARK_BUY = '#00C853';
const MARK_SHORT = '#D50000';
const MARK_EXIT = '#FF8F00';

const DAY = 86400;

/** Pinned to the owner's template values — see the header. */
export const OM_PARAMS = {
  clnLength: 50,
  slowPeriod: 100,
  mmCandleType: 'CLH' as const,
  /** avBand.effectiveMode 'logic1' */
  effLogic2: false,
  /** 30 bars at 4H = 5 days. The backtest measured longer holds doing WORSE:
   *  the 60-bar cap scored +0.23% out-of-sample against +0.47% for 30. */
  maxBars: 30,
  /** Om is measured at 4H. Sub-4H loses to costs on this asset class (already
   *  established by the Bhairav study); above 4H it is simply untested. */
  minTfMinutes: 240,
  maxTfMinutes: 240,
};

export type OmRegime = 'UP' | 'DOWN' | 'NEUTRAL';
export type OmPos = 'LONG' | 'SHORT' | 'FLAT';
export type OmAction = 'BUY' | 'SHORT' | 'SELL' | 'COVER';

/** Om's reason codes — mapped to plain English in signalLabels.ts. */
export type OmReason = 'OM_FLIP_UP' | 'OM_FLIP_DOWN' | 'OM_MM_STOP' | 'OM_TIME' | 'OM_REGIME';

export interface OmStep {
  time: number;
  pos: OmPos;
  signal: OmAction | null;
  reason: OmReason | null;
  /** The active stop level while in a position, for the tooltip. */
  stop: number | null;
}

/** The AV_Band Effective Line — a WVol spike latches its high/low as the zone,
 *  the zone carries until the next spike, and the OverView score picks the zone
 *  HIGH (>+20) / LOW (<-20) / midpoint. */
function effectiveLine(bars: CandleRow[], scoreByTime: Map<number, number>, effLogic2: boolean): number[] {
  const n = bars.length;
  const wv = computeWVol(bars);
  const eff = new Array<number>(n).fill(NaN);
  let zHigh = NaN, zLow = NaN;
  for (let i = 0; i < n; i++) {
    if (wv[i]?.isSpike) { zHigh = Number(bars[i].high); zLow = Number(bars[i].low); }
    if (!Number.isFinite(zHigh) || !Number.isFinite(zLow)) continue;
    const sc = scoreByTime.get(bars[i].time as number);
    if (sc != null && sc > 20) eff[i] = effLogic2 ? zLow : zHigh;
    else if (sc != null && sc < -20) eff[i] = effLogic2 ? zHigh : zLow;
    else eff[i] = (zHigh + zLow) / 2;
  }
  return eff;
}

/** The regime rule — TradingChart `slowHueAt`, exactly. */
export function omRegimeAt(lr: number, slow: number, prevSlow: number): OmRegime {
  if (!Number.isFinite(slow) || !Number.isFinite(prevSlow) || !Number.isFinite(lr)) return 'NEUTRAL';
  if (lr > slow && slow > prevSlow) return 'UP';
  if (lr < slow && slow < prevSlow) return 'DOWN';
  return 'NEUTRAL';
}

/** MM staircase: the body of the last candle that was a BBR reversal or a volume
 *  spike, held flat until the next one. Source type is fixed to CLH. */
function mmSeries(bars: CandleRow[], candleType: string): Array<{ idx: number; upper: number; lower: number } | null> {
  const out: Array<{ idx: number; upper: number; lower: number } | null> = new Array(bars.length).fill(null);
  if (bars.length <= 5) return out;
  const src = candleType === 'Regular' ? bars : transformCandles(bars, candleType as any);
  const bbr = computeBbrReversals(src);
  const spikes = computeVolumeSpikes(src);
  let held: { idx: number; upper: number; lower: number } | null = null;
  for (let i = 0; i < src.length; i++) {
    const t = src[i].time as number;
    if (bbr.overboughtAt.has(t) || bbr.oversoldAt.has(t) || spikes.has(t)) {
      held = {
        idx: i,
        upper: Math.max(Number(src[i].open), Number(src[i].close)),
        lower: Math.min(Number(src[i].open), Number(src[i].close)),
      };
    }
    out[i] = held;
  }
  return out;
}

/** Last HTF bar whose CLOSE is at or before each TF bar's close. */
function asOfIdx(tf: CandleRow[], tfMinutes: number, htf: CandleRow[], htfDurSec: number): number[] {
  const out = new Array<number>(tf.length).fill(-1);
  let j = -1;
  for (let i = 0; i < tf.length; i++) {
    const decision = (tf[i].time as number) + tfMinutes * 60;
    while (j + 1 < htf.length && (htf[j + 1].time as number) + htfDurSec <= decision) j++;
    out[i] = j;
  }
  return out;
}

export interface OmComputed {
  steps: OmStep[];
  regime: OmRegime[];
  firstIdx: number;
  /** false when the daily gate had no usable history — entries are suppressed. */
  gateOk: boolean;
}

/** The whole state machine. Pure; mirrors backend omRules + omSim. */
export function computeOmSteps(
  bars: CandleRow[], scoreByTime: Map<number, number>, dailyRaw: CandleRow[] | undefined, tfMinutes: number,
): OmComputed {
  const n = bars.length;
  const eff = effectiveLine(bars, scoreByTime, OM_PARAMS.effLogic2);
  const slow = hmaSeries(eff, OM_PARAMS.slowPeriod);
  const lr = linreg(bars.map(b => Number(b.close)), OM_PARAMS.clnLength);

  const regime: OmRegime[] = new Array(n).fill('NEUTRAL');
  for (let i = 0; i < n; i++) regime[i] = omRegimeAt(lr[i], slow[i], i > 0 ? slow[i - 1] : NaN);

  let firstIdx = 0;
  while (firstIdx < n && !Number.isFinite(slow[firstIdx])) firstIdx++;
  const warm = Math.max(firstIdx + 20, 60);

  // Daily CLN gate — causal, forming daily bar dropped.
  const dailyDir = new Array<number>(n).fill(0);
  const daily = dropFormingHtfBars((dailyRaw || []).slice().sort((a, b) => (a.time as number) - (b.time as number)), 1440);
  const gateOk = daily.length >= 30;
  if (gateOk) {
    const dCln = computeClnBars(daily, OM_PARAMS.clnLength);
    const dIdx = asOfIdx(bars, tfMinutes, daily, DAY);
    for (let i = 0; i < n; i++) {
      const j = dIdx[i];
      if (j >= 0 && j < dCln.length) dailyDir[i] = dCln[j].up ? 1 : -1;
    }
  }

  const mm = mmSeries(bars, OM_PARAMS.mmCandleType);

  /** first bar of a fresh directional run — a dip into NEUTRAL and back the SAME
   *  way is not a new turn, so it does not re-enter. */
  const isFlip = (i: number): boolean => {
    const r = regime[i];
    if (r === 'NEUTRAL' || i <= firstIdx) return false;
    for (let j = i - 1; j >= firstIdx; j--) {
      if (regime[j] === 'NEUTRAL') continue;
      return regime[j] !== r;
    }
    return true;
  };

  const steps: OmStep[] = [];
  let pos: OmPos = 'FLAT';
  let sign: 1 | -1 = 1;
  let entryIdx = -1;

  for (let i = 0; i < n; i++) {
    let signal: OmAction | null = null;
    let reason: OmReason | null = null;
    let stop: number | null = null;

    if (i >= warm) {
      if (pos !== 'FLAT') {
        // The stair in force at the PREVIOUS close is what this bar trades
        // against — using this bar's own stair would move the stop after the fact.
        const m = mm[i - 1];
        stop = m ? (sign === 1 ? m.lower : m.upper) : null;
        const hit = stop != null && (sign === 1 ? Number(bars[i].low) <= stop : Number(bars[i].high) >= stop);
        const opposite = sign === 1 ? regime[i] === 'DOWN' : regime[i] === 'UP';
        const timeUp = entryIdx >= 0 && (i - entryIdx) >= OM_PARAMS.maxBars;
        if (hit || opposite || timeUp) {
          signal = sign === 1 ? 'SELL' : 'COVER';
          reason = hit ? 'OM_MM_STOP' : timeUp ? 'OM_TIME' : 'OM_REGIME';
          pos = 'FLAT'; entryIdx = -1; stop = null;
        }
      }
      if (pos === 'FLAT' && !signal) {
        const r = regime[i];
        const dir: 0 | 1 | -1 = r === 'UP' ? 1 : r === 'DOWN' ? -1 : 0;
        if (dir !== 0 && isFlip(i) && gateOk && dailyDir[i] === dir) {
          signal = dir === 1 ? 'BUY' : 'SHORT';
          reason = dir === 1 ? 'OM_FLIP_UP' : 'OM_FLIP_DOWN';
          pos = dir === 1 ? 'LONG' : 'SHORT';
          sign = dir; entryIdx = i;
          const m = mm[i];
          stop = m ? (dir === 1 ? m.lower : m.upper) : null;
        }
      }
    }
    steps.push({ time: bars[i].time as number, pos, signal, reason, stop });
  }

  return { steps, regime, firstIdx, gateOk };
}

export function computeOmOverlay(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
  if (!candles || candles.length < 2) return { candleColors: [], markers: [] };
  const tfMin = opts?.ltfMinutes || 0;

  if (tfMin && (tfMin < OM_PARAMS.minTfMinutes || tfMin > OM_PARAMS.maxTfMinutes)) {
    return {
      candleColors: [],
      markers: [],
      notice: tfMin < OM_PARAMS.minTfMinutes
        ? 'Om trades the 4H chart only — sub-4H entries lose to costs on this asset class (backtested). Switch to 4H to see signals.'
        : 'Om is measured on the 4H chart only. It is not validated above 4H, so it stays silent here rather than guessing.',
    };
  }

  // The Effective Line reads the OverView score EXCLUDING the avBand voter —
  // `scoreBase` — which is exactly what the drawn AV_Band uses. Falling back to
  // `score` would reintroduce the circular avBand→score→avBand dependency.
  const scoreByTime = new Map<number, number>();
  for (const p of (opts?.tfTrendSeries || []) as Array<TrendSeriesPoint & { scoreBase?: number }>) {
    const s = Number.isFinite(p.scoreBase as number) ? (p.scoreBase as number) : p.score;
    if (Number.isFinite(s)) scoreByTime.set(p.time, s);
  }
  if (!scoreByTime.size) {
    return {
      candleColors: [], markers: [],
      notice: 'Om needs the OverView trend score for the AV_Band line and it has not loaded yet.',
    };
  }

  const { steps, gateOk } = computeOmSteps(candles, scoreByTime, opts?.dailyCandles, tfMin || 240);

  const candleColors: CandleColor[] = [];
  const markers: ChartMarker[] = [];
  const signalsByTime: NonNullable<StrategyOverlayResult['signalsByTime']> = new Map();
  const positionByTime: NonNullable<StrategyOverlayResult['positionByTime']> = new Map();

  for (const st of steps) {
    const time = st.time;
    switch (st.signal) {
      case 'BUY':
        markers.push({ time, position: 'belowBar', text: 'B', color: MARK_BUY, shape: 'arrowUp', size: 2 });
        signalsByTime.set(time, { signal: 'BUY', strength: null, pos: 'LONG', reason: st.reason });
        break;
      case 'SHORT':
        markers.push({ time, position: 'aboveBar', text: 'S', color: MARK_SHORT, shape: 'arrowDown', size: 2 });
        signalsByTime.set(time, { signal: 'SHORT', strength: null, pos: 'SHORT', reason: st.reason });
        break;
      case 'SELL':
        markers.push({ time, position: 'aboveBar', text: 'X', color: MARK_EXIT, shape: 'circle' });
        signalsByTime.set(time, { signal: 'EXIT_LONG', strength: null, pos: 'FLAT', reason: st.reason });
        break;
      case 'COVER':
        markers.push({ time, position: 'belowBar', text: 'C', color: MARK_EXIT, shape: 'circle' });
        signalsByTime.set(time, { signal: 'EXIT_SHORT', strength: null, pos: 'FLAT', reason: st.reason });
        break;
      default:
        break;
    }
    const col = st.pos === 'LONG' ? GREEN : st.pos === 'SHORT' ? RED : NEUTRAL;
    candleColors.push({ time, upColor: col, downColor: col });
    positionByTime.set(time, st.pos);
  }

  const notice = !gateOk
    ? 'Om needs Daily history for its higher-timeframe gate — daily data unavailable, entries suppressed.'
    : undefined;

  return { candleColors, markers, signalsByTime, positionByTime, notice };
}

export const omStrategy: ChartStrategy = {
  id: 'om',
  name: 'Om',
  computeOverlays(candles: CandleRow[], opts?: ChartStrategyOptions): StrategyOverlayResult {
    return computeOmOverlay(candles, opts);
  },
};
